import { useState } from 'react';
import { KeyRound, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const CheckoutPasswordSetup = ({ onComplete }) => {
  const { setInitialPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await setInitialPassword(password);
      toast.success('Password saved. You can continue checkout now.');
      onComplete?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to save your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white border-stone/50" data-testid="checkout-password-setup-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full p-2" style={{ background: 'rgba(163,106,58,0.12)' }}>
            <KeyRound className="h-4 w-4" style={{ color: '#A36A3A' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ink" data-testid="checkout-password-setup-title">Create a password for your new checkout account</h3>
            <p className="text-xs text-muted-foreground mt-1">
              You signed in with a magic link. Set a password now so you can come back to this checkout and future orders more easily.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-password-setup-form">
          <div>
            <Label htmlFor="checkout-new-password" className="text-xs">New Password</Label>
            <Input
              id="checkout-new-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Create a password"
              className="mt-1"
              required
              data-testid="checkout-password-setup-input"
            />
          </div>
          <div>
            <Label htmlFor="checkout-confirm-password" className="text-xs">Confirm Password</Label>
            <Input
              id="checkout-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Confirm your password"
              className="mt-1"
              required
              data-testid="checkout-password-confirm-input"
            />
          </div>
          {error && (
            <Alert variant="destructive" data-testid="checkout-password-setup-error-alert">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={loading} className="gap-2 rounded-[12px] h-11" data-testid="checkout-password-setup-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? 'Saving...' : 'Save Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};